import { Autocomplete, MenuItem, Select, TextField } from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntryField, EntryTitle, EntryWrapper } from "../../../components/customStyles/form";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import ICleaningSchedule from "../../../models/cleaningSchedule";
import IFloor from "../../../models/floor";
import IProfile from "../../../models/profile";
import ILocalEditRequest from "../../../models/requests/local/localEditRequest";
import macAddressUtils from "../../../utils/macAddressUtils";
import LocalAlarmField from "./LocalAlarmField";
import LocalCleaning from "./LocalCleaning";

const SERVICE_PLUS_STICKER_CODE_REGEX = /^\d{9}$|^$/;

export const isServicePlusStickerCode = (code: string) => {
  return SERVICE_PLUS_STICKER_CODE_REGEX.test(code);
}
interface FloorFormFieldsProps {
  localState: ILocalEditRequest
  setLocalState: Dispatch<
    SetStateAction<ILocalEditRequest>
  >;
  buildings: IAdminPageBuildingDetail[];
  buildingFloors: IFloor[];
  buildingProfiles: IProfile[];
  localCleaning: ICleaningSchedule;
  getAlarmCode?: () => void;
}

const PATTERN_ADDRESS_MAC = /[^A-Fa-f0-9]/g;
const LENGTH_ADDRESS_MAC = 12;
const MAX_LENGTH_SPECIFICATION = 300;

const LocalFormFields = ({
  localState,
  setLocalState,
  buildings,
  buildingFloors,
  buildingProfiles,
  localCleaning,
  getAlarmCode,
}: FloorFormFieldsProps) => {
  const { t } = useTranslation();
  const [codeInput, setCodeInput] = useState("");
  const [routeInput, setRouteInput] = useState("");
  const codes = (localState.codes ?? []).filter(
    (c): c is string => typeof c === "string" && c.trim().length > 0
  );

  const updateLocalState = <T extends keyof ILocalEditRequest>(
    field: T,
    value: ILocalEditRequest[T]
  ) => {
    setLocalState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const onChangeMacAddress = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = (event.target.value || "").toLowerCase();

    const cleanedInput = input
      .replace(PATTERN_ADDRESS_MAC, "")
      .substring(0, LENGTH_ADDRESS_MAC);

    updateLocalState("tagMacAddress", cleanedInput || undefined);
  };

  const onChangeServicePlusStickerCode = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = (event.target.value || "").trim();

    updateLocalState("servicePlusStickerCode", input || undefined);
  };

  const setLocalCleaning = (localCleaning: ICleaningSchedule) => {
    setLocalState((prevState) => ({
      ...prevState,
      localCleaning
    }));
  }
  return (
    <>
      <EntryWrapper>
        <EntryTitle>
          {t("table.local.name")} *
        </EntryTitle>
        <EntryField>
          <TextField
            required={true}
            style={{ textAlign: "left", width: "100%" }}
            value={localState.name}
            onChange={(e) => {
              updateLocalState(
                "name",
                e.target.value
              );
            }}
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.local.building")} *</EntryTitle>
        <EntryField>
          <Select
            required={true}
            value={localState.buildingId}
            onChange={(e) =>
              updateLocalState(
                "buildingId",
                Number(e.target.value)
              )
            }
            sx={{ width: "100%", height: "2em" }}
            MenuProps={{
              style: { overflowY: "visible" },
            }}
          >
            {buildings && buildings.map(
              (building, index) =>
                <MenuItem
                  key={index}
                  value={building.id}
                >
                  {building.name}
                </MenuItem>
            )}
          </Select>
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.local.floor")} *</EntryTitle>
        <EntryField>
          <Select
            required={true}
            value={localState.floorId}
            onChange={(e) =>
              updateLocalState(
                "floorId",
                Number(e.target.value)
              )
            }
            sx={{ width: "100%", height: "2em" }}
            MenuProps={{
              style: { overflowY: "visible" },
            }}
          >
            {buildingFloors && buildingFloors.map(
              (floor, index) =>
                <MenuItem
                  key={index}
                  value={floor.id}
                >
                  {floor.name}
                </MenuItem>
            )}
          </Select>
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.local.profile")} *</EntryTitle>
        <EntryField>
          <Select
            required={true}
            value={localState.profileId}
            onChange={(e) =>
              updateLocalState(
                "profileId",
                Number(e.target.value)
              )
            }
            sx={{ width: "100%", height: "2em" }}
            MenuProps={{
              style: { overflowY: "visible" },
            }}
          >
            {buildingProfiles && buildingProfiles.map(
              (profile, index) =>
                <MenuItem
                  key={index}
                  value={profile.id}
                >
                  {profile.name}
                </MenuItem>
            )}
          </Select>
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>Codes *</EntryTitle>
        <EntryField>
          <Autocomplete
            fullWidth
            multiple
            freeSolo
            value={codes}
            options={[]}
            getOptionLabel={(option) => option}
            inputValue={codeInput}
            onInputChange={(_, value) => setCodeInput(value)}
            onChange={(_, value) => {
              updateLocalState("codes", value as string[]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                onBlur={(event) => {
                  const value = event.target.value.trim();
                  if (!value || codes.includes(value)) return;

                  updateLocalState("codes", [...codes, value]);
                  setCodeInput("");
                }}
              />
            )}
          />

        </EntryField>
      </EntryWrapper>


      <EntryWrapper>
        <EntryTitle>
          {t('table.local.specification')}
        </EntryTitle>
        <EntryField>
          <TextField
            required={false}
            style={{ textAlign: "left", width: "100%" }}
            value={localState.specification}
            inputProps={{ maxLength: MAX_LENGTH_SPECIFICATION }}
            error={
              (localState.specification?.length || 0) > MAX_LENGTH_SPECIFICATION
            }
            helperText={t('table.local.specificationHelper').replace("${0}", `${MAX_LENGTH_SPECIFICATION}`)}
            onChange={(e) => updateLocalState("specification", e.target.value)}
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>
          {t("table.local.tagMacAddress")}
        </EntryTitle>
        <EntryField>
          <TextField
            required={false}
            style={{ textAlign: "left", width: "100%" }}
            value={localState.tagMacAddress}
            error={
              (localState.tagMacAddress?.length ?? 0) > 0 &&
              !macAddressUtils.isMacAdrress(localState.tagMacAddress ?? "")
            }
            helperText={!macAddressUtils.MAC_ADDRESS_REGEX.test(localState.tagMacAddress ?? '') ? t("table.local.macAddressErrMsg") : ""}
            onChange={onChangeMacAddress}
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>
          {t("table.local.servicePlusStickerCode")}
        </EntryTitle>
        <EntryField>
          <TextField
            required={false}
            style={{ textAlign: "left", width: "100%" }}
            value={localState.servicePlusStickerCode}
            error={
              (localState.servicePlusStickerCode?.length ?? 0) > 0 &&
              !isServicePlusStickerCode(localState.servicePlusStickerCode ?? "")
            }
            helperText={!isServicePlusStickerCode(localState.servicePlusStickerCode ?? '') ? t("table.local.servicePlusStickerCodeErrMsg") : ""}
            onChange={onChangeServicePlusStickerCode}
          />
        </EntryField>
      </EntryWrapper>

      <LocalAlarmField
        value={localState.alarmCode}
        onChange={(value) => updateLocalState("alarmCode", value)}
        getAlarmCode={getAlarmCode}
      />


      <EntryWrapper>
        <EntryTitle>
          {t("table.local.minLocalCleaning")}
        </EntryTitle>
        <EntryField>
          <LocalCleaning localCleaning={localCleaning} setLocalCleaning={setLocalCleaning} />
        </EntryField>
      </EntryWrapper>
    </>
  );
};

export default LocalFormFields;
